import type React from 'react'
import { SelectableCard } from '@astryxdesign/core/SelectableCard'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { Token } from '@astryxdesign/core/Token'
import { storyCoverClass } from './publishedStoryLinks'

interface CoverStyleGalleryProps {
  /** Already sorted by the caller, e.g. via `sortCoverStyles`. */
  styles: CoverStyle[]
  /** Which style keys to tag "Suits your genres". */
  suitedKeys: Set<string>
  selectedKey: string | null
  onSelect: (key: string | null) => void
  isDisabled?: boolean
}

/**
 * A single-select grid of cover style options: a sample image (or, absent one, the feed's
 * gradient swatch) plus label, description, and a "Suits your genres" token for styles the
 * caller flagged as matching the story's genre. Clicking the already-selected card clears it.
 */
export default function CoverStyleGallery({
  styles,
  suitedKeys,
  selectedKey,
  onSelect,
  isDisabled = false
}: CoverStyleGalleryProps): React.JSX.Element {
  return (
    <section className="cover-style-gallery" aria-label="Cover styles">
      {styles.map((style) => (
        <SelectableCard
          key={style.key}
          label={`${style.label}: ${style.description}`}
          isSelected={selectedKey === style.key}
          onChange={(selected) => onSelect(selected ? style.key : null)}
          isDisabled={isDisabled}
        >
          <VStack gap={2}>
            {style.imageUrl ? (
              <img
                className="cover-style-sample"
                src={style.imageUrl}
                alt={`${style.label} sample`}
              />
            ) : (
              <span
                className={`cover-style-sample ${storyCoverClass(style.position)}`}
                aria-hidden="true"
              />
            )}
            <HStack gap={2} align="center" justify="between" wrap="wrap">
              <Text type="label">{style.label}</Text>
              {suitedKeys.has(style.key) && (
                <Token label="Suits your genres" size="sm" color="green" />
              )}
            </HStack>
            <Text type="supporting" maxLines={2}>
              {style.description}
            </Text>
          </VStack>
        </SelectableCard>
      ))}
    </section>
  )
}
